// React / Next
import React, { ReactElement } from "react";
// App
import { Step } from "./stepper.interfaces";

/**
 * Hook for managing steps in pages
 */
const useStepper = (initialStep = 0, shouldScrollTopOnChangeStep = true ): { 
  activeStep: number, 
  handleNextStep: () => void, 
  handlePreviousStep: () => void,
  handleStep: (index: number) => void,
  getCurrentStepTemplate: (steps: Step[]) => ReactElement, 
  reset: () => void,
} => {
  const [activeStep, setActiveStep] = React.useState(initialStep);

  /**
   * Go to previous step
   */
  const handlePreviousStep = () => {
    activeStep > 0 && setActiveStep(activeStep -1);
  };

  /**
   * Go to next step
   */
  const handleNextStep = () => {
    shouldScrollTopOnChangeStep && window.scrollTo(0, 0);

    setActiveStep(activeStep +1);
  };

  /**
   * Go to specific step
   */
  const handleStep = (index: number) => {
    shouldScrollTopOnChangeStep && window.scrollTo(0, 0);

    setActiveStep(index);
  };

  /**
   * Reset to first step
   */
  const reset = () => {
    setActiveStep(0);
  };

  /**
   * Returns the template of the current step according to active state
   * @param steps The steps definitions 
   */
  const getCurrentStepTemplate = (steps: Step[]) => {
    const stepsNumber = steps.length;
    let currentStep = activeStep;

    // robustness for inconsistent active step numbers
    if (activeStep < 0) {
      currentStep = 0;
    } else if (activeStep > stepsNumber - 1) {
      currentStep = stepsNumber - 1;
    }

    return steps[currentStep]?.template;
  };

  return { activeStep, handlePreviousStep, handleNextStep, handleStep, getCurrentStepTemplate, reset }
}

export default useStepper;
